import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import apiClient, { orEnCours as api, users as usersApiCore } from '../services/api';
import referentielsApi from '../services/referentiels';
import carnetCommandeApi from '../services/carnet-commande';
import InlineStatusSelect from './InlineStatusSelect';
import ORModal from './ORModal';
import { PlanificationCell, useDossierPlanning } from './shared/DossierPlanification';
import './ORENCours.css';
import './CarnetCommande.css';

const STATUTS_FALLBACK = [
  { value: 'en_cours',      label: 'En cours',             color: '#2563eb' },
  { value: 'attente_pieces',label: 'Attente pièces',        color: '#f59e0b' },
  { value: 'contacte',      label: 'Client contacté',       color: '#8b5cf6' },
  { value: 'bloquant',      label: 'Point bloquant',        color: '#ef4444' },
  { value: 'rdv_pris',      label: 'RDV pris',              color: '#10b981' },
  { value: 'surveiller',    label: 'À surveiller',          color: '#f97316' },
];

const STATUT_LIVRAISON_PIECES = {
  livre:                { label: 'Reçu',               color: '#22c55e', bg: '#22c55e18' },
  recu_partiellement:   { label: 'Reçu partiellement', color: '#f59e0b', bg: '#f59e0b18' },
  livre_partiellement:  { label: 'Reçu partiellement', color: '#f59e0b', bg: '#f59e0b18' },
  en_commande:          { label: 'En commande',        color: '#3b82f6', bg: '#3b82f618' },
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}
function formatMontant(m) {
  if (m == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(m);
}

// ─── Ligne de mapping éditable ──────────────────────────────────────────────
function MappingRow({ mapping, crmUsers, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(mapping.nom_canonique);

  const filteredUsers = search.trim()
    ? crmUsers.filter(u => `${u.nom} ${u.prenom}`.toLowerCase().includes(search.toLowerCase()))
    : crmUsers;

  const handleSave = () => {
    if (selected && selected !== mapping.nom_canonique) {
      onUpdate(mapping.alias, selected);
    }
    setEditing(false);
    setSearch('');
  };

  const handleCancel = () => {
    setSelected(mapping.nom_canonique);
    setEditing(false);
    setSearch('');
  };

  return (
    <tr>
      <td className="orc-corr-alias">{mapping.alias}</td>
      <td className="orc-corr-arrow-cell">→</td>
      <td className="orc-corr-canonical">
        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <input
              type="text"
              placeholder="Rechercher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
              style={{ padding: '4px 8px', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 12 }}
            />
            <select
              value={selected}
              onChange={e => setSelected(e.target.value)}
              size={Math.min(filteredUsers.length + 1, 6) || 3}
              style={{ padding: '4px 8px', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 12 }}
            >
              <option value="">— Sélectionner —</option>
              {filteredUsers.map(u => (
                <option key={u.id} value={`${u.nom} ${u.prenom}`}>
                  {u.nom} {u.prenom}{u.fonction ? ` — ${u.fonction}` : ''}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={handleSave} disabled={!selected || selected === mapping.nom_canonique} style={{ padding: '3px 10px', fontSize: 12, background: '#1e293b', color: 'var(--text-inverse)', border: 'none', borderRadius: 6, cursor: 'pointer' }}>✓ OK</button>
              <button onClick={handleCancel} style={{ padding: '3px 10px', fontSize: 12, background: 'var(--bg-muted)', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer' }}>Annuler</button>
            </div>
          </div>
        ) : (
          <span onClick={() => setEditing(true)} style={{ cursor: 'pointer' }} title="Cliquer pour modifier">
            {mapping.nom_canonique} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>✏️</span>
          </span>
        )}
      </td>
      <td>
        <button className="orc-corr-btn-del" onClick={() => onDelete(mapping.alias)}>🗑️</button>
      </td>
    </tr>
  );
}

export default function ORENCours({ user, onOpenVehicule = () => {}, editMode = false, setHeaderAction }) {
  const isAdmin = user.role === 'super-admin' || user.role === 'admin';
  const isSuperAdmin = user.role === 'super-admin';

  // ── Onglet principal ──
  const [mainTab, setMainTab] = useState('liste');

  // ── Filtres ──
  const [search,         setSearch]         = useState('');
  const [filterStatut,   setFilterStatut]   = useState('');
  const [filterSociete,  setFilterSociete]  = useState(user?.concession_nom || '');
  const [filterReceptionnaire, setFilterReceptionnaire] = useState('');
  const [filterPieces,       setFilterPieces]       = useState('');
  const [filterWithComments, setFilterWithComments] = useState(false);
  const [filterDateFrom,     setFilterDateFrom]     = useState('');
  const [filterDateTo,       setFilterDateTo]       = useState('');
  const [page,           setPage]           = useState(1);
  const [pageSize,       setPageSize]        = useState(() => {
    const v = parseInt(localStorage.getItem('orc-page-size'), 10);
    return [50, 100, 200, 500].includes(v) ? v : 50;
  });

  // ── Données liste ──
  const [ors,      setOrs]      = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(false);

  // ── Dashboard ──
  const [dashboard,        setDashboard]        = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [filterDbSocietes, setFilterDbSocietes] = useState([]); // multi-select concessions

  // ── Onglet Rapport ──
  const [rapportRows,     setRapportRows]     = useState([]);
  const [rapportLoading,  setRapportLoading]  = useState(false);
  const [rapportConcession,     setRapportConcession]     = useState('');
  const [rapportReceptionnaire, setRapportReceptionnaire] = useState('');

  // ── Liste des concessions (pour le filtre Société du tableau détaillé) ──
  const [concessionsList, setConcessionsList] = useState([]);
  useEffect(() => {
    apiClient.get('/concessions')
      .then(r => setConcessionsList(r.data.concessions || []))
      .catch(() => setConcessionsList([]));
  }, []);

  // ── Correspondances ──
  const [mappings,        setMappings]        = useState([]);
  const [receptDistincts, setReceptDistincts] = useState([]);
  const [newAliases,      setNewAliases]      = useState([]);
  const [newCanonique,    setNewCanonique]    = useState('');
  const [savingMapping,   setSavingMapping]   = useState(false);
  const [filterAlias,     setFilterAlias]     = useState('');
  const [crmUsers,        setCrmUsers]        = useState([]);
  const [userSearch,      setUserSearch]      = useState('');

  // ── Tri ──
  const [sortCol, setSortCol] = useState('date_creation');
  const [sortDir, setSortDir] = useState('desc');

  // ── Statuts dynamiques ──
  const [statuts, setStatuts] = useState(STATUTS_FALLBACK);

  // ── Sync ──
  const [syncing,    setSyncing]    = useState(false);
  const [syncMsg,    setSyncMsg]    = useState('');
  const [syncError,  setSyncError]  = useState('');

  // Boutons d'action dans le header — via ref pour toujours utiliser les filtres courants
  const headerActionsRef = React.useRef({ print: () => {}, exportExcel: () => {} });
  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction({
      secondary: [
        { label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Export Excel</>, onClick: () => headerActionsRef.current.exportExcel() },
        { label: <>🖨️ Imprimer</>, onClick: () => headerActionsRef.current.print() },
      ]
    });
  }, [setHeaderAction]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chargement des statuts depuis les référentiels ──
  useEffect(() => {
    referentielsApi.getStatutsOrEnCours()
      .then(res => {
        const data = res.data.statuts_or_en_cours || [];
        if (data.length > 0) {
          setStatuts(data.map(s => ({ value: s.code, label: s.nom, color: s.couleur })));
        }
      })
      .catch(e => console.error('Erreur chargement statuts OR:', e));
  }, []);

  const statutLabel = useCallback((val) => {
    return statuts.find(s => s.value === val)?.label || val;
  }, [statuts]);

  const statutColor = useCallback((val) => {
    return statuts.find(s => s.value === val)?.color || '#6b7280';
  }, [statuts]);

  // ── Modal OR ──
  const [selectedOrId, setSelectedOrId] = useState(null);

  // ── Modal Pièces en commande ──
  const [piecesModal, setPiecesModal] = useState(null);
  const [piecesModalLoading, setPiecesModalLoading] = useState(false);

  const LIMIT = pageSize;

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (search)               params.search         = search;
      if (filterStatut)         params.statut         = filterStatut;
      if (filterSociete)        params.societe        = filterSociete;
      if (filterReceptionnaire) params.receptionnaire = filterReceptionnaire;
      if (filterDateFrom)       params.date_from      = filterDateFrom;
      if (filterDateTo)         params.date_to        = filterDateTo;
      if (filterWithComments)   params.with_comments  = 'true';
      const r = await api.getAll(params);
      setOrs(r.data.ors);
      setTotal(r.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, filterStatut, filterSociete, filterReceptionnaire, filterDateFrom, filterDateTo, filterWithComments]);

  useEffect(() => { loadList(); }, [loadList]);

  // Synchroniser le filtre société avec la concession globale
  useEffect(() => {
    setFilterSociete(user?.concession_nom || '');
  }, [user?.concession_nom]);

  // ── Chargement dashboard ─────────────────────────────────────────────────────
  useEffect(() => {
    if (mainTab !== 'dashboard') return;
    setLoadingDashboard(true);
    api.getDashboard()
      .then(r => setDashboard(r.data))
      .catch(err => console.error(err))
      .finally(() => setLoadingDashboard(false));
  }, [mainTab]);

  // ── Chargement rapport (tous les OR du périmètre) ────────────────────────────
  useEffect(() => {
    if (mainTab !== 'rapport') return;
    setRapportLoading(true);
    api.exportAll({})
      .then(r => setRapportRows(r.data.ors || []))
      .catch(err => console.error(err))
      .finally(() => setRapportLoading(false));
  }, [mainTab]);

  // ── Chargement correspondances ───────────────────────────────────────────────
  const loadMappings = () => {
    api.getMappings().then(r => setMappings(r.data.mappings)).catch(console.error);
  };
  useEffect(() => {
    if (mainTab !== 'correspondances') return;
    loadMappings();
    api.getReceptionnairesDistincts().then(r => setReceptDistincts(r.data.receptionnaires)).catch(console.error);
    usersApiCore.getAll().then(r => {
      const sorted = (r.data.users || r.data || [])
        .filter(u => u.actif !== false)
        .sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`));
      setCrmUsers(sorted);
    }).catch(console.error);
  }, [mainTab]);

  const handleCreateMapping = async () => {
    if (!newAliases.length || !newCanonique.trim()) return;
    setSavingMapping(true);
    try {
      await Promise.all(newAliases.map(alias =>
        api.createMapping({ alias, nom_canonique: newCanonique.trim() })
      ));
      setNewAliases([]); setNewCanonique('');
      loadMappings();
      if (dashboard) api.getDashboard().then(r => setDashboard(r.data)).catch(console.error);
      loadList();
    } catch (err) { console.error(err); }
    finally { setSavingMapping(false); }
  };

  const handleUpdateMapping = async (alias, newNomCanonique) => {
    if (!newNomCanonique.trim()) return;
    try {
      await api.createMapping({ alias, nom_canonique: newNomCanonique.trim() });
      loadMappings();
      if (dashboard) api.getDashboard().then(r => setDashboard(r.data)).catch(console.error);
      loadList();
    } catch (err) { console.error(err); }
  };

  const handleDeleteMapping = async (alias) => {
    if (!window.confirm(`Supprimer la correspondance "${alias}" ?`)) return;
    try {
      await api.deleteMapping(alias);
      loadMappings();
      if (dashboard) api.getDashboard().then(r => setDashboard(r.data)).catch(console.error);
      loadList();
    } catch (err) { console.error(err); }
  };

  // Reset page quand filtre change
  useEffect(() => { setPage(1); }, [search, filterStatut, filterSociete, filterReceptionnaire, filterPieces, filterWithComments, filterDateFrom, filterDateTo, pageSize]);
  useEffect(() => { localStorage.setItem('orc-page-size', String(pageSize)); }, [pageSize]);

  // ── Impression PDF ──────────────────────────────────────────────────────────
  const [printing, setPrinting] = useState(false);

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const params = {};
      if (search)               params.search         = search;
      if (filterStatut)         params.statut         = filterStatut;
      if (filterSociete)        params.societe        = filterSociete;
      if (filterReceptionnaire) params.receptionnaire = filterReceptionnaire;
      if (filterDateFrom)       params.date_from      = filterDateFrom;
      if (filterDateTo)         params.date_to        = filterDateTo;
      if (filterWithComments)   params.with_comments  = 'true';
      const r = await api.exportAll(params);
      const rows = r.data.ors;

      const statuts = { en_cours:'En cours', attente_pieces:'Attente pièces', contacte:'Client contacté', bloquant:'Point bloquant', rdv_pris:'RDV pris', surveiller:'À surveiller' };
      const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
      const fmtMt   = m => m == null ? '—' : new Intl.NumberFormat('fr-FR', { style:'currency', currency:'EUR', maximumFractionDigits:0 }).format(m);

      const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8">
        <title>OR en cours — ${new Date().toLocaleDateString('fr-FR')}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 10px; color: #111; padding: 16px; }
          h1 { font-size: 14px; margin-bottom: 4px; }
          .meta { font-size: 9px; color: #666; margin-bottom: 12px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #1e40af; color: white; padding: 5px 4px; text-align: left; font-size: 9px; }
          td { padding: 4px; border-bottom: 1px solid #e5e7eb; vertical-align: middle; }
          tr:nth-child(even) td { background: #f8fafc; }
          .badge { display:inline-block; padding:2px 5px; border-radius:3px; font-size:8px; font-weight:bold; }
          .st-en_cours      { background:#dbeafe; color:#1e40af; }
          .st-attente_pieces{ background:#fef3c7; color:#92400e; }
          .st-contacte      { background:#ede9fe; color:#5b21b6; }
          .st-bloquant      { background:#fee2e2; color:#991b1b; }
          .st-rdv_pris      { background:#d1fae5; color:#065f46; }
          .st-surveiller    { background:#ffedd5; color:#9a3412; }
          .center { text-align: center; }
          .right  { text-align: right; }
          @page { size: A4 landscape; margin: 10mm; }
          @media print { body { padding: 0; } }
        </style>
      </head><body>
        <h1>OR en cours — Export du ${new Date().toLocaleDateString('fr-FR')}</h1>
        <div class="meta">${rows.length} OR${rows.length > 1 ? 's' : ''}${search ? ` · Recherche : "${search}"` : ''}${filterStatut ? ` · Statut : ${statuts[filterStatut] || filterStatut}` : ''}${filterSociete ? ` · Société : ${filterSociete}` : ''}${filterReceptionnaire ? ` · Réceptionnaire : ${filterReceptionnaire}` : ''}</div>
        <table>
          <thead><tr>
            <th>Ouverture</th><th>N° OR</th><th>Client</th><th>Immat</th>
            <th>Réceptionnaire</th><th>Société</th><th>CIGA</th>
            <th class="right">Lignes</th><th class="right">Montant HT</th>
            <th class="center">PD</th><th class="center">P</th>
            <th>Pièce en cmd</th><th>Statut</th>
          </tr></thead>
          <tbody>
            ${rows.map(o => `<tr>
              <td>${fmtDate(o.date_creation)}</td>
              <td>${o.num_or || o.id_or || '—'}</td>
              <td>${o.nom_client || '—'}</td>
              <td>${o.immat || '—'}</td>
              <td>${o.receptionnaire || '—'}</td>
              <td>${o.societe_nom || o.societe || '—'}</td>
              <td>${o.ciga || '—'}</td>
              <td class="right">${o.nb_lignes ?? '—'}</td>
              <td class="right">${fmtMt(o.montant_ht)}</td>
              <td class="center">${parseFloat(o.mvt_oui_non) > 0 ? '🔩' : ''}</td>
              <td class="center">${o.in_parking ? '🅿️' : ''}</td>
              <td>${o.pieces_statut_livraison ? ({ en_commande: 'En commande', recu_partiellement: 'Reçu part.', livre: 'Reçu' }[o.pieces_statut_livraison] || '—') : '—'}</td>
              <td><span class="badge st-${o.statut || ''}">${statuts[o.statut] || o.statut || '—'}</span></td>
            </tr>`).join('')}
          </tbody>
        </table>
      </body></html>`;

      const w = window.open('', '_blank', 'width=1200,height=800');
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 500);
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la génération de l\'impression');
    } finally {
      setPrinting(false);
    }
  };

  // ── Export Excel (tous les OR du périmètre filtré) ───────────────────────────
  const [exporting, setExporting] = useState(false);
  const buildFilterParams = () => {
    const params = {};
    if (search)               params.search         = search;
    if (filterStatut)         params.statut         = filterStatut;
    if (filterSociete)        params.societe        = filterSociete;
    if (filterReceptionnaire) params.receptionnaire = filterReceptionnaire;
    if (filterDateFrom)       params.date_from      = filterDateFrom;
    if (filterDateTo)         params.date_to        = filterDateTo;
    if (filterWithComments)   params.with_comments  = 'true';
    return params;
  };
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const r = await api.exportAll(buildFilterParams());
      let rows = r.data.ors || [];
      if (filterPieces) {
        rows = rows.filter(o => filterPieces === 'aucune' ? !o.pieces_statut_livraison : o.pieces_statut_livraison === filterPieces);
      }
      if (rows.length === 0) { alert('Aucun OR à exporter pour les filtres sélectionnés.'); return; }
      const labelPieces = (s) => s === 'livre' ? 'Reçu' : s === 'recu_partiellement' || s === 'livre_partiellement' ? 'Reçu partiellement' : s === 'en_commande' ? 'En commande' : '';
      const wsData = [
        ['Ouverture', 'N° OR', 'Client', 'Immat', 'Réceptionnaire', 'Société', 'Lignes', 'Montant HT', 'Mouvement', 'Parking', 'Pièces', 'Statut'],
        ...rows.map(o => [
          o.date_creation ? new Date(o.date_creation).toLocaleDateString('fr-FR') : '',
          o.num_or || o.id_or || '',
          o.nom_client || '',
          o.immat || '',
          o.receptionnaire || '',
          o.societe_nom || o.societe || '',
          o.nb_lignes != null ? Number(o.nb_lignes) : '',
          o.montant_ht != null ? Number(o.montant_ht) : '',
          parseFloat(o.mvt_oui_non) > 0 ? 'Oui' : '',
          o.in_parking ? 'Oui' : '',
          labelPieces(o.pieces_statut_livraison),
          statutLabel(o.statut),
        ])
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [10, 12, 22, 11, 18, 18, 7, 11, 10, 8, 16, 16].map(w => ({ wch: w }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'OR en cours');
      // Téléchargement via Blob explicite (plus fiable que XLSX.writeFile, qui
      // échoue silencieusement dans certains navigateurs/extensions après un await).
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `OR_en_cours_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Erreur export OR:', err);
      alert('Erreur lors de l\'export Excel : ' + (err?.response?.data?.error || err?.message || 'erreur inconnue'));
    } finally {
      setExporting(false);
    }
  };

  // Maintient les handlers du header à jour (filtres courants)
  headerActionsRef.current.print = handlePrint;
  headerActionsRef.current.exportExcel = handleExportExcel;

  // ── Sync ────────────────────────────────────────────────────────────────────
  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    setSyncError('');
    try {
      const r = await api.sync();
      setSyncMsg(r.data.message || 'Synchronisation réussie');
      loadList();
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  // ── Tri client-side ──────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const filteredOrs = filterPieces
    ? ors.filter(o => {
        if (filterPieces === 'aucune') return !o.pieces_statut_livraison;
        return o.pieces_statut_livraison === filterPieces;
      })
    : ors;

  const PIECES_ORDER = { en_commande: 1, recu_partiellement: 2, livre: 3 };
  const STATUT_ORDER = { en_cours: 1, attente_pieces: 2, contacte: 3, bloquant: 4, rdv_pris: 5, surveiller: 6 };

  const sortedOrs = [...filteredOrs].sort((a, b) => {
    let valA = a[sortCol], valB = b[sortCol];
    if (sortCol === 'date_creation' || sortCol === 'date_planification') {
      valA = new Date(valA || 0).getTime();
      valB = new Date(valB || 0).getTime();
    } else if (sortCol === 'montant_ht' || sortCol === 'nb_lignes') {
      valA = parseFloat(valA || 0);
      valB = parseFloat(valB || 0);
    } else if (sortCol === 'mvt_oui_non') {
      valA = parseFloat(valA || 0);
      valB = parseFloat(valB || 0);
    } else if (sortCol === 'in_parking') {
      valA = valA ? 1 : 0;
      valB = valB ? 1 : 0;
    } else if (sortCol === 'pieces_statut_livraison') {
      valA = PIECES_ORDER[valA] || 0;
      valB = PIECES_ORDER[valB] || 0;
    } else if (sortCol === 'statut') {
      valA = STATUT_ORDER[valA] || 99;
      valB = STATUT_ORDER[valB] || 99;
    } else {
      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
    }
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortTh = ({ col, children, className }) => (
    <th
      onClick={() => handleSort(col)}
      className={`orc-sort-th ${sortCol === col ? 'active' : ''} ${className || ''}`}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {children} {sortCol === col && <span>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  // ── Mise à jour statut inline (sans ouvrir la modale) ──────────────────────
  const handleUpdateStatutInline = async (id, newStatut) => {
    try {
      await api.updateStatut(id, { statut: newStatut });
      setOrs(prev => prev.map(o => o.id === id ? { ...o, statut: newStatut } : o));
    } catch (err) {
      console.error(err);
    }
  };

  // Colonne Planification : etat, chargement et modale viennent du module
  // partage (sept ecrans la rendent a l'identique).
  const { ouvrir: ouvrirDossierPlanning, modale: modaleDossier } = useDossierPlanning({
    user, editMode, onEnregistre: () => loadList(),
  });

  // ── Callback après sauvegarde statut/action depuis ORModal ──────────────────
  const handleStatutChange = (id, statut, action) => {
    setOrs(prev => prev.map(o => o.id === id ? { ...o, statut, action } : o));
  };

  const handleOpenPiecesModal = async (numOr, e) => {
    e.stopPropagation();
    setPiecesModalLoading(true);
    try {
      const res = await carnetCommandeApi.getByOr(numOr);
      if (res.data.found) {
        setPiecesModal(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setPiecesModalLoading(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);


  return (
    <div className="page-container">

      {/* ── Onglets principaux ──────────────────────────────────────────── */}
      <div className="page-tabs">
        {[
          { id: 'liste',           label: '📋 Liste des ORs' },
          { id: 'rapport',         label: '📑 Rapport' },
          { id: 'correspondances', label: '🔗 Correspondances' },
          { id: 'dashboard',       label: '📊 Tableau de bord' },
        ].map(t => (
          <button
            key={t.id}
            className={mainTab === t.id ? 'active' : ''}
            onClick={() => setMainTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ DASHBOARD ══════════════════════════════════════════════════════ */}
      {mainTab === 'dashboard' && (
        <div className="orc-dashboard">
          {loadingDashboard && <div className="orc-loading-full">Chargement du tableau de bord…</div>}
          {!loadingDashboard && dashboard && (() => {
            const { cigas, par_receptionnaire } = dashboard;

            // Liste des sociétés pour le filtre (toutes les societes de toutes les lignes)
            const societes = [...new Set(par_receptionnaire.flatMap(r => r.societes || []))].sort();

            // Lignes filtrées : réceptionnaire présent dans au moins une des sociétés sélectionnées
            const rows = filterDbSocietes.length > 0
              ? par_receptionnaire.filter(r => filterDbSocietes.some(s => (r.societes || []).includes(s)))
              : par_receptionnaire;

            const toggleSociete = (s) =>
              setFilterDbSocietes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

            // Total général sur les lignes filtrées
            const totalFiltre = rows.reduce((s, r) => s + r.total, 0);
            const montantFiltre = rows.reduce((s, r) => s + r.montant_total, 0);

            return (
              <>
                {/* ── Filtre concession (multi) ── */}
                <div className="orc-db-toolbar">
                  <div className="orc-db-chips">
                    <button
                      className={`orc-db-chip${filterDbSocietes.length === 0 ? ' active' : ''}`}
                      onClick={() => setFilterDbSocietes([])}
                    >
                      Toutes
                    </button>
                    {societes.map(s => (
                      <button
                        key={s}
                        className={`orc-db-chip${filterDbSocietes.includes(s) ? ' active' : ''}`}
                        onClick={() => toggleSociete(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Tableau pivot ── */}
                <div className="orc-db-pivot-wrap">
                  <table className="orc-db-pivot">
                    <thead>
                      <tr>
                        <th>Réceptionnaire</th>
                        {cigas.map(c => <th key={c} className="orc-db-ciga-col">{c}</th>)}
                        <th className="orc-db-total-col">Total</th>
                        <th className="orc-db-montant-col">Montant HT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => (
                        <tr key={r.receptionnaire} className="orc-db-row">
                          <td className="orc-db-recep-cell">{r.receptionnaire}</td>
                          {cigas.map(c => (
                            <td key={c} className="orc-db-nb-cell">
                              {r.par_ciga[c] ? <span className="orc-db-nb-badge">{r.par_ciga[c]}</span> : <span className="orc-db-nb-empty">—</span>}
                            </td>
                          ))}
                          <td className="orc-db-nb-cell orc-db-total-cell">{r.total}</td>
                          <td className="orc-db-montant-cell">{formatMontant(r.montant_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="orc-db-grand-total-row">
                        <td>TOTAL</td>
                        {cigas.map(c => {
                          const n = rows.reduce((s, r) => s + (r.par_ciga[c] || 0), 0);
                          return <td key={c} className="orc-db-nb-cell">{n || '—'}</td>;
                        })}
                        <td className="orc-db-nb-cell">{totalFiltre}</td>
                        <td className="orc-db-montant-cell">{formatMontant(montantFiltre)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ══ CORRESPONDANCES ════════════════════════════════════════════════ */}
      {mainTab === 'correspondances' && (
        <div className="orc-correspondances">
          {/* Formulaire d'ajout */}
          {(() => {
            const mappedAliases = new Set(mappings.map(m => m.alias));
            const aliasesDisponibles = receptDistincts.filter(r => !mappedAliases.has(r));
            const aliasesFiltres = filterAlias.trim()
              ? aliasesDisponibles.filter(r => r.toLowerCase().includes(filterAlias.toLowerCase()))
              : aliasesDisponibles;
            const filteredUsers = userSearch.trim()
              ? crmUsers.filter(u => `${u.nom} ${u.prenom}`.toLowerCase().includes(userSearch.toLowerCase()))
              : crmUsers;
            return (
              <div className="orc-corr-form">
                <div className="orc-corr-form-title">Ajouter une correspondance</div>
                <div className="orc-corr-form-row">
                  <div className="orc-corr-field">
                    <label>
                      Alias SFTP
                      {newAliases.length > 1 && <span className="orc-corr-count"> ({newAliases.length} sélectionnés)</span>}
                    </label>
                    <input
                      type="text"
                      className="orc-corr-filter-input"
                      placeholder="Filtrer les alias…"
                      value={filterAlias}
                      onChange={e => setFilterAlias(e.target.value)}
                    />
                    <select
                      multiple
                      value={newAliases}
                      onChange={e => setNewAliases([...e.target.selectedOptions].map(o => o.value))}
                      className="orc-corr-select orc-corr-select-multi"
                      size={Math.min(aliasesFiltres.length, 8) || 3}
                    >
                      {aliasesFiltres.length === 0
                        ? <option disabled>{aliasesDisponibles.length === 0 ? 'Tous les alias sont déjà mappés' : 'Aucun résultat'}</option>
                        : aliasesFiltres.map(r => <option key={r} value={r}>{r}</option>)
                      }
                    </select>
                    <span className="orc-corr-hint">Ctrl+clic pour sélection multiple</span>
                  </div>
                  <div className="orc-corr-arrow">→</div>
                  <div className="orc-corr-field">
                    <label>Utilisateur CRM</label>
                    <input
                      type="text"
                      className="orc-corr-filter-input"
                      placeholder="Rechercher un utilisateur…"
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                    />
                    <select
                      value={newCanonique}
                      onChange={e => setNewCanonique(e.target.value)}
                      className="orc-corr-select"
                      size={Math.min(filteredUsers.length + 1, 8) || 3}
                    >
                      <option value="">— Sélectionner —</option>
                      {filteredUsers.map(u => (
                        <option key={u.id} value={`${u.nom} ${u.prenom}`}>
                          {u.nom} {u.prenom}{u.fonction ? ` — ${u.fonction}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    className="orc-corr-btn-add"
                    onClick={handleCreateMapping}
                    disabled={savingMapping || !newAliases.length || !newCanonique.trim()}
                  >
                    {savingMapping ? '⏳' : `➕ Appliquer${newAliases.length > 1 ? ` (${newAliases.length})` : ''}`}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Tableau des mappings */}
          <div className="orc-corr-table-wrap">
            {mappings.length === 0 ? (
              <div className="orc-corr-empty">Aucune correspondance définie. Utilisez le formulaire ci-dessus pour en créer.</div>
            ) : (
              <table className="orc-corr-table">
                <thead>
                  <tr>
                    <th>Alias (fichier SFTP)</th>
                    <th>→</th>
                    <th>Utilisateur CRM</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map(m => (
                    <MappingRow
                      key={m.alias}
                      mapping={m}
                      crmUsers={crmUsers}
                      onUpdate={handleUpdateMapping}
                      onDelete={handleDeleteMapping}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ══ LISTE ══════════════════════════════════════════════════════════ */}
      {mainTab === 'liste' && <>

      {/* ── Barre de contrôle ───────────────────────────────────────────── */}
      <div className="page-toolbar">
        <input
          type="text"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} className={`page-select${filterStatut ? ' active-filter' : ''}`}>
          <option value="">Tous les statuts</option>
          {statuts.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select
          value={filterSociete}
          onChange={e => setFilterSociete(e.target.value)}
          className={`page-select${filterSociete ? ' active-filter' : ''}`}
          style={{ maxWidth: 200 }}
        >
          <option value="">Toutes les sociétés</option>
          {concessionsList.map(c => (
            <option key={c.id} value={c.nom}>{c.nom}</option>
          ))}
        </select>
        <input
          type="text"
          className="page-search"
          placeholder="Réceptionnaire…"
          value={filterReceptionnaire}
          onChange={e => setFilterReceptionnaire(e.target.value)}
          style={{ maxWidth: 160 }}
        />
        <select value={filterPieces} onChange={e => setFilterPieces(e.target.value)} className={`page-select${filterPieces ? ' active-filter' : ''}`}>
          <option value="">Pièces : tous</option>
          <option value="en_commande">En commande</option>
          <option value="recu_partiellement">Reçu partiellement</option>
          <option value="livre">Reçu</option>
          <option value="aucune">Sans commande</option>
        </select>
        <button
          className={`page-select orc-comments-btn${filterWithComments ? ' active-filter' : ''}`}
          onClick={() => setFilterWithComments(v => !v)}
          title="Afficher uniquement les OR avec au moins un commentaire"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        </button>
        <div className="orc-date-filter">
          <label>Du</label>
          <input type="date" className="orc-date-input" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
          <label>au</label>
          <input type="date" className="orc-date-input" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
          {(filterDateFrom || filterDateTo) && (
            <button className="orc-date-clear" onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); }} title="Effacer les dates">✕</button>
          )}
        </div>
      </div>

      {syncMsg   && <div className="orc-alert orc-alert-success">✅ {syncMsg}</div>}
      {syncError && <div className="orc-alert orc-alert-error">❌ {syncError}</div>}

      {/* ── Tableau ─────────────────────────────────────────────────────── */}
      <div className="table-container">
        <table className="orc-table">
          <thead>
            <tr>
              <SortTh col="date_creation">Ouverture</SortTh>
              <SortTh col="num_or">N° OR</SortTh>
              <SortTh col="nom_client">Client</SortTh>
              <SortTh col="immat">Immat</SortTh>
              <SortTh col="receptionnaire">Réceptionnaire</SortTh>
              <SortTh col="nb_lignes">Lignes</SortTh>
              <SortTh col="montant_ht">Montant HT</SortTh>
              <SortTh col="mvt_oui_non">🔩</SortTh>
              <SortTh col="in_parking">🅿️</SortTh>
              <SortTh col="pieces_statut_livraison">Pièce en cmd</SortTh>
              <SortTh col="statut">Statut</SortTh>
              {isSuperAdmin && <SortTh col="date_planification">Planification</SortTh>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={isSuperAdmin ? 12 : 11} className="orc-loading">Chargement…</td></tr>
            )}
            {!loading && sortedOrs.length === 0 && (
              <tr><td colSpan={isSuperAdmin ? 12 : 11} className="orc-empty">
                Aucun OR en cours
              </td></tr>
            )}
            {!loading && sortedOrs.map(or => {
              return (
              <tr key={or.id} className="orc-row" onClick={() => setSelectedOrId(or.id)}>
                <td>{formatDate(or.date_creation)}</td>
                <td className="orc-numor">{or.num_or || or.id_or}</td>
                <td className="orc-client">{or.nom_client || '—'}</td>
                <td className="orc-immat" onClick={e => { e.stopPropagation(); if (or.immat) onOpenVehicule(or.immat); }} style={or.immat ? { cursor: 'pointer' } : {}}>
                  {or.immat
                    ? <span className="plaque-badge plaque-badge-link">{or.immat}</span>
                    : '—'
                  }
                </td>
                <td>{or.receptionnaire || '—'}</td>
                <td className="orc-center">{or.nb_lignes}</td>
                <td className="orc-montant">{formatMontant(or.montant_ht)}</td>
                <td className="orc-center orc-mvt-cell">
                  {parseFloat(or.mvt_oui_non) > 0 ? <span title={`Mvt: ${or.mvt_oui_non}`}>🔩</span> : null}
                </td>
                <td className="orc-center">
                  {or.in_parking ? <span title="Véhicule présent au parking">🅿️</span> : null}
                </td>
                <td className="orc-center" onClick={e => e.stopPropagation()}>
                  {or.pieces_statut_livraison ? (() => {
                    const s = STATUT_LIVRAISON_PIECES[or.pieces_statut_livraison] || STATUT_LIVRAISON_PIECES.en_commande;
                    return (
                      <button
                        className="orc-pieces-btn"
                        style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 6,
                          fontSize: '0.8rem', fontWeight: 600, color: s.color, background: s.bg,
                          border: `1px solid ${s.color}30`, cursor: 'pointer',
                        }}
                        onClick={(e) => handleOpenPiecesModal(or.num_or, e)}
                        title="Voir le détail des pièces commandées"
                      >
                        {piecesModalLoading ? '…' : s.label}
                      </button>
                    );
                  })() : (
                    <span style={{ color: 'var(--text-muted)' }}>—</span>
                  )}
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <InlineStatusSelect
                    value={or.statut}
                    options={statuts.map(s => ({ value: s.value, label: s.label, color: s.color }))}
                    onChange={(newStatut) => handleUpdateStatutInline(or.id, newStatut)}
                  />
                </td>
                {isSuperAdmin && (
                  <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    <PlanificationCell dossier={or} onOuvrir={ouvrirDossierPlanning} />
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      <div className="orc-pagination" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {totalPages > 1 && (
          <>
            <button disabled={page <= 1} onClick={() => setPage(1)} title="Première page">«</button>
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} title="Page précédente">‹</button>
            <span>Page {page} / {totalPages} — {total} OR{total > 1 ? 's' : ''}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} title="Page suivante">›</button>
            <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Dernière page">»</button>
          </>
        )}
        {totalPages <= 1 && total > 0 && (
          <span>{total} OR{total > 1 ? 's' : ''}</span>
        )}
        <label style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Par page
          <select className="page-select" value={pageSize} onChange={e => setPageSize(parseInt(e.target.value, 10))} style={{ height: 30, width: 'auto' }}>
            {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>

      </> /* fin mainTab === 'liste' */}

      {/* ══ RAPPORT ════════════════════════════════════════════════════════ */}
      {mainTab === 'rapport' && (() => {
        // Filtrage concession + réceptionnaire
        let rows = rapportRows;
        if (rapportConcession)     rows = rows.filter(o => (o.societe_nom || o.societe) === rapportConcession);
        if (rapportReceptionnaire) rows = rows.filter(o => o.receptionnaire === rapportReceptionnaire);

        // Listes pour les filtres (sur l'ensemble chargé)
        const concessions = [...new Set(rapportRows.map(o => o.societe_nom || o.societe).filter(Boolean))].sort();
        const receptionnaires = [...new Set(rapportRows.map(o => o.receptionnaire).filter(Boolean))].sort();

        // Blocs du rapport (extensible) — on commence par « Véhicules sur le parking »
        const BLOCS = [
          { id: 'parking', titre: '🅿️ Véhicules sur le parking', match: (o) => !!o.in_parking },
        ];

        return (
          <div className="orc-rapport">
            <div className="page-toolbar">
              <select value={rapportConcession} onChange={e => setRapportConcession(e.target.value)} className={`page-select${rapportConcession ? ' active-filter' : ''}`} style={{ maxWidth: 220 }}>
                <option value="">Toutes les concessions</option>
                {concessions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={rapportReceptionnaire} onChange={e => setRapportReceptionnaire(e.target.value)} className={`page-select${rapportReceptionnaire ? ' active-filter' : ''}`} style={{ maxWidth: 220 }}>
                <option value="">Tous les réceptionnaires</option>
                {receptionnaires.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            {rapportLoading && <div className="orc-loading-full">Chargement du rapport…</div>}

            {!rapportLoading && BLOCS.map(bloc => {
              const blocRows = rows.filter(bloc.match);
              return (
                <div key={bloc.id} className="orc-rapport-bloc" style={{ marginTop: 16 }}>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px' }}>
                    {bloc.titre}
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-muted)', borderRadius: 12, padding: '2px 10px' }}>{blocRows.length}</span>
                  </h3>
                  <div className="table-container">
                    <table className="orc-table">
                      <thead>
                        <tr>
                          <th>Ouverture</th>
                          <th>N° OR</th>
                          <th>Client</th>
                          <th>Immat</th>
                          <th>Réceptionnaire</th>
                          <th>Société</th>
                          <th>Montant HT</th>
                          <th>Pièces</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blocRows.length === 0 ? (
                          <tr><td colSpan={9} className="orc-empty">Aucun OR dans ce bloc</td></tr>
                        ) : blocRows.map(or => (
                          <tr key={or.id} className="orc-row" onClick={() => setSelectedOrId(or.id)}>
                            <td>{formatDate(or.date_creation)}</td>
                            <td className="orc-numor">{or.num_or || or.id_or}</td>
                            <td className="orc-client">{or.nom_client || '—'}</td>
                            <td className="orc-immat">{or.immat ? <span className="plaque-badge">{or.immat}</span> : '—'}</td>
                            <td>{or.receptionnaire || '—'}</td>
                            <td>{or.societe_nom || or.societe || '—'}</td>
                            <td className="orc-montant">{formatMontant(or.montant_ht)}</td>
                            <td className="orc-center">
                              {or.pieces_statut_livraison ? (() => {
                                const s = STATUT_LIVRAISON_PIECES[or.pieces_statut_livraison] || STATUT_LIVRAISON_PIECES.en_commande;
                                return <span style={{ padding: '2px 10px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, color: s.color, background: s.bg, border: `1px solid ${s.color}30` }}>{s.label}</span>;
                              })() : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                            </td>
                            <td><span style={{ color: statutColor(or.statut), fontWeight: 600 }}>{statutLabel(or.statut)}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ══════════════ MODAL PIÈCES EN COMMANDE ═══════════════════════════ */}
      {piecesModal && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="cc-modal-title">OR {piecesModal.num_or}</h3>
                <div className="cc-modal-subtitle">
                  {piecesModal.nom_client && <span>{piecesModal.nom_client}</span>}
                  {piecesModal.immat && <span> · {piecesModal.immat.replace(/[-\s]/g, '')}</span>}
                </div>
              </div>
              <button className="modal-close" onClick={() => setPiecesModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {(!piecesModal.lignes || piecesModal.lignes.length === 0) ? (
                <p className="cc-modal-empty">Aucune ligne de pièce.</p>
              ) : (
                <div className="cc-modal-table-wrap">
                  <table className="cc-modal-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Référence</th>
                        <th>Réf. rempl.</th>
                        <th>Description</th>
                        <th>Fournisseur</th>
                        <th>Type cde</th>
                        <th>N° cde</th>
                        <th>Cdé</th>
                        <th>Qté livrée</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {piecesModal.lignes.map((l, i) => {
                        const s = STATUT_LIVRAISON_PIECES[l.statut_livraison] || STATUT_LIVRAISON_PIECES.en_commande;
                        return (
                          <tr key={i}>
                            <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{i + 1}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{l.reference || '—'}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{l.ref_rempl || '—'}</td>
                            <td>{l.description || '—'}</td>
                            <td>{l.fournisseur || '—'}</td>
                            <td>{l.type_commande || '—'}</td>
                            <td>{l.cde || '—'}</td>
                            <td style={{ textAlign: 'center' }}>{l.cde_qty ?? 0}</td>
                            <td style={{ textAlign: 'center' }}>{l.qte_livree ?? 0}</td>
                            <td>
                              <span style={{
                                display: 'inline-block', padding: '2px 10px', borderRadius: 6,
                                fontSize: '0.8rem', fontWeight: 600, color: s.color, background: s.bg,
                                border: `1px solid ${s.color}30`,
                              }}>
                                {s.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ MODAL ══════════════════════════════════════════════ */}
      {modaleDossier}

      {selectedOrId && (
        <ORModal
          orId={selectedOrId}
          user={user}
          onClose={() => setSelectedOrId(null)}
          onStatutChange={handleStatutChange}
        />
      )}
    </div>
  );
}
